import { create } from 'zustand'
import { dbStorage } from '../lib/indexedDBStorage'
import {
  annotationResearchNoteTargetsMatch,
  archiveAnnotationResearchNote,
} from '../lib/annotationResearchNotes'
import type {
  AnnotationResearchNote,
  AnnotationResearchNoteTarget,
} from '../types/annotationResearchNoteTypes'

const scopeId = (chapterId: string, sourceWitnessId: string): string => `${chapterId}::${sourceWitnessId}`

const sortNotes = (notes: readonly AnnotationResearchNote[]): AnnotationResearchNote[] => notes
  .slice()
  .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id))

interface AnnotationResearchNoteStoreState {
  notesByScope: Map<string, AnnotationResearchNote[]>
  loadingScopeIds: Set<string>
  getScope: (chapterId: string, sourceWitnessId: string) => AnnotationResearchNote[]
  loadScope: (chapterId: string, sourceWitnessId: string) => Promise<AnnotationResearchNote[]>
  saveNote: (note: AnnotationResearchNote) => Promise<void>
  archiveNote: (noteId: string, archivedAt?: string) => Promise<void>
  archiveTarget: (target: AnnotationResearchNoteTarget, archivedAt?: string) => Promise<void>
}

export const useAnnotationResearchNoteStore = create<AnnotationResearchNoteStoreState>((set, get) => ({
  notesByScope: new Map(),
  loadingScopeIds: new Set(),

  getScope: (chapterId, sourceWitnessId) => get().notesByScope.get(scopeId(chapterId, sourceWitnessId)) || [],

  loadScope: async (chapterId, sourceWitnessId) => {
    const id = scopeId(chapterId, sourceWitnessId)
    const cached = get().notesByScope.get(id)
    if (cached) {
      return cached
    }
    set(state => ({ loadingScopeIds: new Set(state.loadingScopeIds).add(id) }))
    try {
      const notes = sortNotes(await dbStorage.getAllByCompoundIndex(
        'annotation_research_notes', 'chapter_source', [chapterId, sourceWitnessId],
      ))
      set(state => ({ notesByScope: new Map(state.notesByScope).set(id, notes) }))
      return notes
    } finally {
      set(state => {
        const loadingScopeIds = new Set(state.loadingScopeIds)
        loadingScopeIds.delete(id)
        return { loadingScopeIds }
      })
    }
  },

  saveNote: async note => {
    await dbStorage.put('annotation_research_notes', note)
    const id = scopeId(note.chapterId, note.sourceWitnessId)
    set(state => {
      const current = state.notesByScope.get(id) || []
      const notes = current.some(candidate => candidate.id === note.id)
        ? current.map(candidate => candidate.id === note.id ? note : candidate)
        : [...current, note]
      return { notesByScope: new Map(state.notesByScope).set(id, sortNotes(notes)) }
    })
  },

  archiveNote: async (noteId, archivedAt) => {
    const located = Array.from(get().notesByScope.entries())
      .flatMap(([id, notes]) => notes.map(note => ({ id, note })))
      .find(item => item.note.id === noteId)
    if (!located || located.note.archivedAt) {
      return
    }
    const archived = archiveAnnotationResearchNote(located.note, archivedAt)
    await dbStorage.put('annotation_research_notes', archived)
    set(state => ({
      notesByScope: new Map(state.notesByScope).set(
        located.id,
        (state.notesByScope.get(located.id) || []).map(note => note.id === noteId ? archived : note),
      ),
    }))
  },

  archiveTarget: async (target, archivedAt) => {
    const matches = Array.from(get().notesByScope.values())
      .flat()
      .filter(note => !note.archivedAt && annotationResearchNoteTargetsMatch(note.target, target))
    for (const note of matches) {
      await get().archiveNote(note.id, archivedAt)
    }
  },
}))
